import { styled } from '@mui/material/styles';
import MuiCard from '@mui/material/Card';
import { memo, type ReactNode } from 'react';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import BrandLockup from '@/components/BrandLockup';
import { useTranslate } from '@/state/i18n';
import { surfaceTokens, radiusTokens } from '@/theme/themePrimitives';

const Card = styled(MuiCard)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignSelf: 'center',
  width: '100%',
  padding: theme.spacing(2),
  gap: theme.spacing(2),
  margin: 0,
  // Same Signal paper as the sign-in card, so every logged-out page sits on the floor alike.
  border: `1px solid ${surfaceTokens.borderPaper}`,
  borderRadius: radiusTokens.xxl,
  boxShadow: surfaceTokens.shadowRaised,
  [theme.breakpoints.up('sm')]: {
    width: '450px',
    padding: theme.spacing(4),
  },
  [theme.breakpoints.up('md')]: {
    margin: 'auto 0',
  },
  ...theme.applyStyles('dark', {
    borderColor: (theme.vars || theme).palette.divider,
    boxShadow:
      'hsla(220, 30%, 5%, 0.5) 0px 5px 15px 0px, hsla(220, 25%, 10%, 0.08) 0px 15px 35px -5px',
  }),
}));

interface Props {
  children: ReactNode;
  redline?: ReactNode;
}

const AuthCardContainer = ({ children, redline }: Props) => {
  const t = useTranslate('domain.auth.components.AuthCardContainer');

  return (
    <Card variant="outlined">
      <BrandLockup descriptor={t('brandTag')} size="bar" />
      <Divider />
      {redline && (
        <Stack sx={{ width: '100%', alignItems: 'flex-start' }} mt={1} mb={2}>
          <Typography component="h2">{redline}</Typography>
        </Stack>
      )}
      {children}
    </Card>
  );
};

export default memo(AuthCardContainer);
